import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { debounceTime, first } from 'rxjs/operators';

import { GetInstructorsByClubsIdsQuery, InstructorModel } from '@bayclubs/group-classes-employee-api';
import { Subscription } from 'rxjs';
import { InstructorsApiService } from '../../services/instructors.api.service';

@Component({
    selector: 'app-instructor-search-component',
    templateUrl: './instructor-search.component.html',
    styleUrls: ['./instructor-search.component.scss'],
    standalone: false
})
export class InstructorSearchComponent implements OnInit, OnDestroy {
    public results: InstructorModel[] = [];
    public searchForm: UntypedFormGroup;
    public searchVisible = false;
    public isBusy = false;

    private subscriptions: Subscription[] = [];

    @Input() dropDownWidth: number = null;

    @Output()
    public instructorSelected: EventEmitter<InstructorModel> = new EventEmitter<InstructorModel>();

    @ViewChild('resultsElement', { static: true }) resultsElement: ElementRef;
    @ViewChild('controlRoot', { static: true }) controlRoot: ElementRef;

    /// According to https://stackoverflow.com/questions/39366981/viewchild-in-ngif
    // sometimes search element was not set
    private searchElement: ElementRef;
    @ViewChild('searchInput') set searchInput(searchInput: ElementRef) {
        if (!!searchInput) {
            this.searchElement = searchInput;
        }
    }

    @Input() public invalid: boolean;

    @Input()
    set clubIds(newClubIds: string[]) {
        this.searchForm.patchValue({ clubsIds: newClubIds });
        this.searchInstructors();
    }

    private _instructorModel: InstructorModel;

    @Input()
    set selectedInstructorModel(newInstructor: InstructorModel) {
        this._instructorModel = newInstructor;
    }

    get selectedInstructorModel(): InstructorModel {
        return this._instructorModel;
    }

    get selectedInstructorName(): string {
        if (this._instructorModel) {
            return this._instructorModel.name;
        }
        return '';
    }

    constructor(private api: InstructorsApiService, formBuilder: UntypedFormBuilder) {
        this.searchForm = formBuilder.group({
            searchPhrase: [null, []],
            clubsIds: [null, []]
        });
    }

    ngOnInit() {
        this.subscribeFormValueChange();

        this.searchInstructors();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    private subscribeFormValueChange() {
        this.subscriptions.push(
            this.searchPhraseControl.valueChanges.pipe(debounceTime(500)).subscribe(() => {
                this.searchInstructors();
            })
        );
    }

    private get searchPhraseControl() {
        return this.searchForm.get('searchPhrase');
    }

    public get hasItems() {
        return this.results && this.results.length > 0;
    }

    private changeSelectedInstructorModel(newModel: InstructorModel) {
        this.instructorSelected.emit(newModel);
        this.selectedInstructorModel = newModel;
    }

    public onItemClick(item: InstructorModel) {
        this.searchVisible = false;
        this.changeSelectedInstructorModel(item);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event) {
        if (!this.isDisabled) {
            if (this.controlRoot.nativeElement.contains(event.target) && !this._instructorModel) {
                this.onEnter();
            } else {
                this.onLeave();
            }
        }
    }

    onSearch() {
        if (this.isBusy) {
            return;
        }
        this.searchInstructors();
    }

    private searchInstructors() {
        if (this.searchForm.valid) {
            this.isBusy = true;

            const request = this.prepareRequest();
            this.api
                .getMany(request)
                .pipe(first())
                .subscribe({
                    next: result => {
                        this.results = result.items;
                    },
                    complete: () => {
                        this.isBusy = false;
                    }
                });
        }
    }

    private prepareRequest(): GetInstructorsByClubsIdsQuery {
        return <GetInstructorsByClubsIdsQuery>this.searchForm.value;
    }

    public get showClearButton(): boolean {
        return !!this._instructorModel;
    }

    public onClear() {
        this.changeSelectedInstructorModel(null);
    }

    public getPhotoUrl(item: InstructorModel) {
        if (!item || !item.photoUrl) {
            return null;
        }

        return item.photoUrl + '?format=raw&&height=32';
    }

    public onFocus() {
        this.onEnter();
    }

    public onEnter() {
        this.searchVisible = true;
        this.focusSearchControl();
    }

    private focusSearchControl() {
        setTimeout(() => {
            if (this.searchElement) {
                this.searchElement.nativeElement.focus();
            }
        }, 0);
    }

    public onLeave() {
        this.searchVisible = false;
    }

    public get isDisabled(): boolean {
        return !this.searchForm.valid || this.isInstructorSelected;
    }

    public get isInstructorSelected(): boolean {
        return !!this._instructorModel;
    }

    public get isDropDownWidthProvided(): boolean {
        return this.dropDownWidth !== null;
    }
}
